import { PeriodEntry, CycleSettings, PredictedPeriod } from "../types";

/**
 * Predict the next period based on the most recent past period + cycle length.
 * Returns null if no past periods exist or a period is currently active.
 * Only returns 1 prediction — when a new period is logged, the prediction shifts.
 */
export const getNextPrediction = (
  periodEntries: PeriodEntry[],
  settings: CycleSettings
): PredictedPeriod | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use only past periods (ignore future entries)
  const sorted = [...periodEntries]
    .filter((e) => new Date(e.startDate + "T00:00:00") <= today)
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  if (sorted.length === 0) return null;

  let cycleLength = settings.avgCycleLength || 28;
  let periodLength = settings.avgPeriodLength || 5;

  // Calculate actual averages if we have enough history
  // Use completed entries (with endDate) for cycle/period length averages
  const completedEntries = sorted.filter((e) => !!e.endDate);
  if (completedEntries.length >= 2) {
    const cycleLengths: number[] = [];
    for (let i = 0; i < completedEntries.length - 1; i++) {
      const current = new Date(completedEntries[i].startDate);
      const next = new Date(completedEntries[i + 1].startDate);
      const diff = (current.getTime() - next.getTime()) / (1000 * 60 * 60 * 24);
      cycleLengths.push(diff);
    }
    cycleLength = Math.round(
      cycleLengths.reduce((sum, len) => sum + len, 0) / cycleLengths.length
    );
  } else if (sorted.length >= 2) {
    const cycleLengths: number[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = new Date(sorted[i].startDate);
      const next = new Date(sorted[i + 1].startDate);
      const diff = (current.getTime() - next.getTime()) / (1000 * 60 * 60 * 24);
      cycleLengths.push(diff);
    }
    cycleLength = Math.round(
      cycleLengths.reduce((sum, len) => sum + len, 0) / cycleLengths.length
    );
  }

  if (completedEntries.length > 0) {
    const periodLengths = completedEntries.map((e) => {
      const start = new Date(e.startDate);
      const end = new Date(e.endDate!);
      return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    });
    periodLength = Math.round(
      periodLengths.reduce((sum, len) => sum + len, 0) / periodLengths.length
    );
  }

  const lastStart = new Date(sorted[0].startDate + "T00:00:00");
  const nextStart = new Date(lastStart);
  nextStart.setDate(nextStart.getDate() + cycleLength);

  const nextEnd = new Date(nextStart);
  nextEnd.setDate(nextEnd.getDate() + periodLength - 1);

  return { startDate: nextStart, endDate: nextEnd, confidence: sorted.length >= 3 ? 0.8 : 0.5 };
};

export type CyclePhase = "menstrual" | "follicular" | "ovulation" | "luteal";

export interface CyclePhaseInfo {
  phase: CyclePhase;
  dayInCycle: number;
  daysUntilNextPeriod: number;
  label: string;
  description: string;
}

export interface OvulationWindow {
  start: Date;
  end: Date;
}

/**
 * Determine the current cycle phase based on period history and settings.
 * Typical 28-day cycle phases:
 *  - Menstrual: days 1–5 (period length)
 *  - Follicular: days 6–13
 *  - Ovulation: days 14–16 (around day 14)
 *  - Luteal: days 17–28
 */
export const getCyclePhase = (
  periodEntries: PeriodEntry[],
  settings: CycleSettings
): CyclePhaseInfo | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Only consider periods that have already started (ignore future entries)
  const sorted = [...periodEntries]
    .filter((e) => new Date(e.startDate + "T00:00:00") <= today)
    .sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  if (sorted.length === 0) return null;

  const lastStart = new Date(sorted[0].startDate + "T00:00:00");

  const dayInCycle =
    Math.floor((today.getTime() - lastStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  const cycleLength = settings.avgCycleLength || 28;
  const periodLength = settings.avgPeriodLength || 5;
  const ovulationDay = Math.round(cycleLength - 14); // ~14 days before next period
  const daysUntilNextPeriod = Math.max(0, cycleLength - dayInCycle + 1);

  // If past the expected cycle length, treat as a new menstrual phase
  const effectiveDay = dayInCycle > cycleLength ? ((dayInCycle - 1) % cycleLength) + 1 : dayInCycle;

  let phase: CyclePhase;
  let label: string;
  let description: string;

  if (effectiveDay <= periodLength) {
    phase = "menstrual";
    label = "Menstrual Phase";
    description = "Your period — rest and take it easy.";
  } else if (effectiveDay < ovulationDay - 4) {
    phase = "follicular";
    label = "Follicular Phase";
    description = "Energy rising — great time for new projects.";
  } else if (effectiveDay <= ovulationDay + 1) {
    phase = "ovulation";
    label = "Fertile Window";
    description = "Peak fertility — highest chance of conception.";
  } else {
    phase = "luteal";
    label = "Luteal Phase";
    description = "Winding down — focus on self-care.";
  }

  return { phase, dayInCycle: effectiveDay, daysUntilNextPeriod, label, description };
};

/**
 * Calculate the fertile window around ovulation.
 * Medically, the fertile window is ~6 days: 5 days before ovulation + ovulation day.
 * Sperm survive up to 5 days; the egg survives 12-24 hours after ovulation.
 * We use days -4 to +1 relative to ovulation to account for timing uncertainty.
 */
export const getOvulationWindow = (
  periodEntries: PeriodEntry[],
  settings: CycleSettings
): OvulationWindow | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Only consider periods that have already started
  const sorted = [...periodEntries]
    .filter((e) => new Date(e.startDate + "T00:00:00") <= today)
    .sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  if (sorted.length === 0) return null;

  const lastStart = new Date(sorted[0].startDate + "T00:00:00");
  const cycleLength = settings.avgCycleLength || 28;
  const ovulationDay = Math.round(cycleLength - 14);

  const start = new Date(lastStart);
  start.setDate(start.getDate() + ovulationDay - 4); // 4 days before ovulation

  const end = new Date(lastStart);
  end.setDate(end.getDate() + ovulationDay + 1); // 1 day after ovulation

  return { start, end };
};

/**
 * Calculate ovulation windows for the current cycle AND predicted future cycles.
 */
export const getOvulationWindows = (
  periodEntries: PeriodEntry[],
  predictions: PredictedPeriod[],
  settings: CycleSettings
): OvulationWindow[] => {
  const windows: OvulationWindow[] = [];
  const cycleLength = settings.avgCycleLength || 28;
  const ovulationDay = Math.round(cycleLength - 14);

  // Current cycle (from most recent period)
  const current = getOvulationWindow(periodEntries, settings);
  if (current) windows.push(current);

  // Predicted future cycles
  for (const pred of predictions) {
    const predStart = new Date(pred.startDate);
    const start = new Date(predStart);
    start.setDate(start.getDate() + ovulationDay - 4);
    const end = new Date(predStart);
    end.setDate(end.getDate() + ovulationDay + 1);
    windows.push({ start, end });
  }

  return windows;
};

export const isPredictedPeriodDay = (
  day: Date,
  predictions: PredictedPeriod[]
): PredictedPeriod | null => {
  const dayTime = day.getTime();
  for (const pred of predictions) {
    if (dayTime >= pred.startDate.getTime() && dayTime <= pred.endDate.getTime()) {
      return pred;
    }
  }
  return null;
};
